import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Noty from 'noty';
import UiIcon from '../../../../uq-ui/components/ui-icon';
import UQButton from '../../../../uq-ui/components/button';
import ConfirmModal from '../../../../uq-ui/components/confirm-modal';
import stickerRepository from '../../../domain/repositories/StickerRepository';
import { loadSticker } from '../../../store/entities/stickers/actions';
import { loadStickerItems } from '../../../store/entities/sticker-items/actions';
import styles from './style.css';

type Action = 'reset' | 'toMemorial' | 'toGreeting';

/**
 * Moderator (and admin) cog on the sticker page: Reset llama, and switch the
 * sticker between Greeting and Memorial. Every action is destructive or
 * changes what the owner can do, so each goes through a ConfirmModal.
 * The server enforces the same roles (POST v2/stickers/:id/clean and /type).
 */
// `onDone` replaces the default refresh (reload the sticker page's data): the
// setup flow restarts instead, since a reset or a new type changes the flow.
export default function ModeratorCog({ stickerId, type, onDone }: { stickerId: string, type?: string, onDone?: () => void }) {
  const { t } = useTranslation('moderator-cog');
  const dispatch = useDispatch();
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState<Action | null>(null);
  const [busy, setBusy] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const isMemorial = type === 'MEMORIAL';

  // While the menu is open, a tap anywhere outside it (or Escape) closes it.
  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: Event) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  const switchAction: Action = isMemorial ? 'toGreeting' : 'toMemorial';

  const choose = (action: Action) => {
    setOpen(false);
    setPending(action);
  };

  const run = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      if (pending === 'reset') {
        await stickerRepository.cleanSticker(stickerId);
      } else {
        await stickerRepository.setStickerType(stickerId, pending === 'toMemorial' ? 'MEMORIAL' : 'GREETING');
      }
      setPending(null);
      if (onDone) {
        onDone();
      } else {
        dispatch(loadSticker(stickerId));
        dispatch(loadStickerItems(stickerId));
      }
    } catch (e) {
      new Noty({ type: 'error', timeout: 3000, text: t('failed') }).show();
    } finally {
      setBusy(false);
    }
  };

  const modal = pending && {
    reset: { title: t('resetTitle'), message: t('resetMessage'), confirm: t('resetConfirm'), theme: 'danger' },
    toMemorial: { title: t('toMemorialTitle'), message: t('toMemorialMessage'), confirm: t('switchConfirm'), theme: 'danger' },
    toGreeting: { title: t('toGreetingTitle'), message: t('toGreetingMessage'), confirm: t('switchConfirm'), theme: 'danger' },
  }[pending];

  return (
    <>
      <div className={styles.anchor} ref={anchorRef}>
        {open && (
          <div className={styles.popover} role="menu">
            <button type="button" role="menuitem" className={`${styles.item} font-text`} onClick={() => choose(switchAction)}>
              <UiIcon name="swap" size={20} />
              <span>{isMemorial ? t('toGreetingMenu') : t('toMemorialMenu')}</span>
            </button>
            <button type="button" role="menuitem" className={`${styles.item} ${styles.danger} font-text`} onClick={() => choose('reset')}>
              <UiIcon name="refresh" size={20} />
              <span>{t('resetMenu')}</span>
            </button>
          </div>
        )}
        <button type="button" aria-label={t('menuLabel')} aria-expanded={open}
                className={styles.cog} onClick={() => setOpen(!open)}>
          <UiIcon name={open ? 'close' : 'cog'} size={24} />
        </button>
      </div>
      <ConfirmModal
        show={!!pending}
        title={modal ? modal.title : ''}
        styles={{ zIndex: 110 }}
        showConfirmBtn={false}
        onClose={() => !busy && setPending(null)}>
        {modal && (
          <div className={styles.modalBody}>
            <div className="font-text">{modal.message}</div>
            <UQButton label={modal.confirm} theme={modal.theme} disabled={busy} onPress={run} />
          </div>
        )}
      </ConfirmModal>
    </>
  );
}
